
import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { resolveAnyStaticUrl } from '../../../urlConfig';
import { ensureBoxProjectedUVs } from '../../../utils/three/ensureBoxProjectedUVs';
import {
    getViewerTheme,
    normalizeMaterials,
    isValidDimensions,
} from './adminProduct3DPreview.utils';
import {
    applySurfaceFinishToThreeMaterial,
    SURFACE_FINISH,
    getEffectiveSurfaceFinish,
    normalizeSurfaceFinishPolicy,
    ensureGranitePhysicalMaterial,
} from './graniteSurfaceFinish.utils';
import {
    normalizeAdminProduct3DSceneSettings,
} from './adminProduct3DSceneSettings';

const qualityConfig = {
    low: {
        pixelRatio: 1,
        shadowSize: 1024,
        shadowType: THREE.PCFShadowMap,
    },
    medium: {
        pixelRatio: 1.5,
        shadowSize: 2048,
        shadowType: THREE.PCFShadowMap,
    },
    high: {
        pixelRatio: 2,
        shadowSize: 4096,
        shadowType: THREE.PCFShadowMap,
    },
};

const CAMERA_INTERACTION_SETTLE_MS = 140;

const MAX_PANORAMA_WIDTH = 4096;

const limitPanoramaTextureSize = (texture) => {
    const image = texture?.image;
    const width = Number(image?.naturalWidth || image?.width || 0);
    const height = Number(image?.naturalHeight || image?.height || 0);

    if (
        !width
        || !height
        || width <= MAX_PANORAMA_WIDTH
        || typeof document === 'undefined'
    ) {
        return texture;
    }

    const nextWidth = MAX_PANORAMA_WIDTH;
    const nextHeight = Math.max(
        1,
        Math.round(height * (nextWidth / width))
    );
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: false });

    if (!context) return texture;

    canvas.width = nextWidth;
    canvas.height = nextHeight;
    context.drawImage(image, 0, 0, nextWidth, nextHeight);
    texture.image = canvas;

    return texture;
};

const clamp = (value, min, max, fallback = min) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return fallback;
    }

    return Math.min(max, Math.max(min, number));
};

const getSunPosition = (settings, distance) => {
    const angle = THREE.MathUtils.degToRad(Number(settings.sunAngle || 0));
    const vertical = THREE.MathUtils.degToRad(Number(settings.sunVertical || 35));
    const radius = Math.max(distance, 1);

    return new THREE.Vector3(
        Math.cos(angle) * Math.cos(vertical) * radius,
        Math.sin(vertical) * radius,
        Math.sin(angle) * Math.cos(vertical) * radius
    );
};

const getSafeValue = (value, fallback) => {
    const number = Number(value);

    if (!Number.isFinite(number) || number <= 0) {
        return fallback;
    }

    return number;
};

const normalizeModelFileType = (value = '') => (
    String(value || '').trim().toLowerCase()
);

const modelUsesFrontendQuarterTurn = (fileType = '') => (
    ['obj', 'glb', 'gltf'].includes(normalizeModelFileType(fileType))
);

/**
 * На основном frontend вся сцена имеет -90° по X, а OBJ/GLB/GLTF
 * внутри ModelInstance получают компенсирующие +90°. Итоговый world-поворот
 * этих форматов равен нулю. Админский viewport не имеет общего sceneRoot,
 * поэтому здесь нельзя безусловно ставить -90° всем моделям.
 *
 * STL на основном frontend компенсации не получает, поэтому для него
 * сохраняем -90° и переводим Z-up геометрию в Y-up сцену Three.js.
 */
const getAdminModelRotationX = (fileType = '') => (
    modelUsesFrontendQuarterTurn(fileType) ? 0 : -Math.PI / 2
);

const getDimensionOffset = (x, y, z) => {
    const maxSize = Math.max(x, y, z, 0.1);

    return clamp(maxSize * 0.025, 0.025, 0.045, 0.035);
};

const getSemanticDimensions = (dimensions, fallbackSize) => {
    if (!isValidDimensions(dimensions)) {
        return {
            width: fallbackSize.x || 1,
            height: fallbackSize.y || 1,
            thickness: fallbackSize.z || 0.08,
        };
    }

    return {
        width: getSafeValue(dimensions.x, fallbackSize.x || 1),
        height: getSafeValue(dimensions.y, fallbackSize.y || 1),
        thickness: getSafeValue(dimensions.z, fallbackSize.z || 0.08),
    };
};

const detectAxisPreset = (baseSize, explicitPreset) => {
    if (explicitPreset === 'vertical' || explicitPreset === 'horizontal') {
        return explicitPreset;
    }

    const x = Math.max(Number(baseSize.x) || 0, 0.001);
    const y = Math.max(Number(baseSize.y) || 0, 0.001);
    const z = Math.max(Number(baseSize.z) || 0, 0.001);
    const max = Math.max(x, y, z);

    if (y <= max * 0.35 && z >= max * 0.65) {
        return 'horizontal';
    }

    return 'vertical';
};

const semanticToSceneDimensions = (semantic, preset) => {
    if (preset === 'horizontal') {
        return {
            x: semantic.width,
            y: semantic.thickness,
            z: semantic.height,
        };
    }

    return {
        x: semantic.width,
        y: semantic.height,
        z: semantic.thickness,
    };
};

const getScaleToTargetBounds = (sourceSize, targetSize) => {
    return {
        x: getSafeValue(targetSize.x / Math.max(sourceSize.x, 0.000001), 1),
        y: getSafeValue(targetSize.y / Math.max(sourceSize.y, 0.000001), 1),
        z: getSafeValue(targetSize.z / Math.max(sourceSize.z, 0.000001), 1),
    };
};

const getSceneAxisLabels = (semantic, preset) => {
    if (preset === 'horizontal') {
        return {
            x: `${Math.round(semantic.width * 1000)} мм`,
            y: `${Math.round(semantic.thickness * 1000)} мм`,
            z: `${Math.round(semantic.height * 1000)} мм`,
        };
    }

    return {
        x: `${Math.round(semantic.width * 1000)} мм`,
        y: `${Math.round(semantic.height * 1000)} мм`,
        z: `${Math.round(semantic.thickness * 1000)} мм`,
    };
};

const getDimensionVisualTheme = (viewMode = 'light') => {
    if (viewMode === 'dark') {
        return {
            line: '#c7d2fe',
            labelText: '#f8fafc',
            labelStroke: 'rgba(2, 6, 23, 0.98)',
            labelShadow: 'rgba(0, 0, 0, 0.72)',
        };
    }

    if (viewMode === 'studio') {
        return {
            line: '#d97706',
            labelText: '#f8fafc',
            labelStroke: 'rgba(2, 6, 23, 0.98)',
            labelShadow: 'rgba(0, 0, 0, 0.68)',
        };
    }

    return {
        line: '#312e81',
        labelText: '#111827',
        labelStroke: 'rgba(255, 255, 255, 0.98)',
        labelShadow: 'rgba(15, 23, 42, 0.48)',
    };
};

const makeTextSprite = (
    text,
    visualTheme = getDimensionVisualTheme('light'),
    labelScale = 1.55
) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fontSize = 62;
    const horizontalPadding = 18;
    const verticalPadding = 16;
    const outlineWidth = 9;
    const font = `900 ${fontSize}px Arial, sans-serif`;

    ctx.font = font;
    const measuredWidth = Math.ceil(ctx.measureText(text).width);

    canvas.width = Math.max(
        128,
        measuredWidth + horizontalPadding * 2 + outlineWidth * 2
    );
    canvas.height = fontSize + verticalPadding * 2 + outlineWidth * 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    ctx.shadowColor = visualTheme.labelShadow;
    ctx.shadowBlur = 7;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    ctx.lineWidth = outlineWidth;
    ctx.strokeStyle = visualTheme.labelStroke;
    ctx.strokeText(text, canvas.width / 2, canvas.height / 2);

    ctx.shadowBlur = 4;
    ctx.fillStyle = visualTheme.labelText;
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
    });

    const sprite = new THREE.Sprite(material);
    const safeScale = clamp(labelScale, 0.8, 3, 1.55);
    const spriteHeight = 0.046 * safeScale;
    const spriteAspect = canvas.width / canvas.height;

    sprite.renderOrder = 40;
    sprite.scale.set(spriteHeight * spriteAspect, spriteHeight, 1);
    sprite.userData.__adminLabelTexture = texture;

    return sprite;
};

const createLine = (start, end, color = '#0f172a') => {
    const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);

    const material = new THREE.LineBasicMaterial({
        color,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 1,
        toneMapped: false,
    });

    const line = new THREE.Line(geometry, material);
    line.renderOrder = 18;

    return line;
};

const createTickLine = (center, direction, size, color = '#0f172a') => {
    const half = size / 2;

    const start = center.clone().add(direction.clone().multiplyScalar(-half));
    const end = center.clone().add(direction.clone().multiplyScalar(half));

    return createLine(start, end, color);
};

const getSafeBounds = (bounds, fallbackSize) => {
    if (bounds?.isBox3 && !bounds.isEmpty()) {
        return bounds.clone();
    }

    const x = Math.max(Number(fallbackSize?.x) || 1, 0.01);
    const y = Math.max(Number(fallbackSize?.y) || 1, 0.01);
    const z = Math.max(Number(fallbackSize?.z) || 0.08, 0.01);

    return new THREE.Box3(
        new THREE.Vector3(-x / 2, -y / 2, -z / 2),
        new THREE.Vector3(x / 2, y / 2, z / 2)
    );
};

const createHorizontalDimensionGroup = (
    bounds,
    labels,
    fallbackSize,
    visualTheme = getDimensionVisualTheme('light'),
    labelScale = 1.55,
    options = {}
) => {
    const group = new THREE.Group();
    const lineColor = visualTheme.line;
    const box = getSafeBounds(bounds, fallbackSize);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    const opposite = options.placement === 'opposite';
    const visualScale = clamp(options.visualScale, 0.55, 1.2, 1);

    box.getSize(size);
    box.getCenter(center);

    /*
      Для лежачей плиты:
      X = ширина
      Y = толщина
      Z = длина / высота изделия

      Линейки заготовки можно увести на противоположные грани. Так размеры
      модели и рамки не накладываются друг на друга даже при близких размерах.
    */
    const offset = getDimensionOffset(size.x, size.y, size.z) * visualScale;
    const tick = Math.max(offset * 0.9, 0.022 * visualScale);
    const lineY = box.min.y + Math.min(Math.max(size.y * 0.22, 0.012), 0.028);
    const textLift = Math.max(size.y * 0.5, 0.04) * visualScale;
    const edgeZ = opposite ? box.max.z + offset : box.min.z - offset;
    const surfaceZ = opposite ? box.max.z : box.min.z;
    const lengthX = opposite ? box.max.x + offset : box.min.x - offset;
    const thicknessX = opposite ? box.min.x - offset : box.max.x + offset;
    const thicknessZ = opposite ? box.max.z : box.min.z;

    const widthStart = new THREE.Vector3(box.min.x, lineY, edgeZ);
    const widthEnd = new THREE.Vector3(box.max.x, lineY, edgeZ);
    const widthText = makeTextSprite(labels.x, visualTheme, labelScale);
    widthText.position.set(
        center.x,
        lineY + textLift,
        edgeZ + (opposite ? offset * 0.55 : -offset * 0.55)
    );

    group.add(
        createLine(widthStart, widthEnd, lineColor),
        createLine(
            new THREE.Vector3(box.min.x, lineY, surfaceZ),
            widthStart,
            lineColor
        ),
        createLine(
            new THREE.Vector3(box.max.x, lineY, surfaceZ),
            widthEnd,
            lineColor
        ),
        createTickLine(widthStart, new THREE.Vector3(0, 0, 1), tick, lineColor),
        createTickLine(widthEnd, new THREE.Vector3(0, 0, 1), tick, lineColor),
        widthText
    );

    const lengthStart = new THREE.Vector3(lengthX, lineY, box.min.z);
    const lengthEnd = new THREE.Vector3(lengthX, lineY, box.max.z);
    const lengthText = makeTextSprite(labels.z, visualTheme, labelScale);
    lengthText.position.set(
        lengthX + (opposite ? offset * 0.62 : -offset * 0.62),
        lineY + textLift,
        center.z
    );

    group.add(
        createLine(lengthStart, lengthEnd, lineColor),
        createLine(
            new THREE.Vector3(opposite ? box.max.x : box.min.x, lineY, box.min.z),
            lengthStart,
            lineColor
        ),
        createLine(
            new THREE.Vector3(opposite ? box.max.x : box.min.x, lineY, box.max.z),
            lengthEnd,
            lineColor
        ),
        createTickLine(lengthStart, new THREE.Vector3(1, 0, 0), tick, lineColor),
        createTickLine(lengthEnd, new THREE.Vector3(1, 0, 0), tick, lineColor),
        lengthText
    );

    const thicknessStart = new THREE.Vector3(thicknessX, box.min.y, thicknessZ);
    const thicknessEnd = new THREE.Vector3(thicknessX, box.max.y, thicknessZ);
    const thicknessText = makeTextSprite(labels.y, visualTheme, labelScale);
    thicknessText.position.set(
        thicknessX + (opposite ? -offset * 0.58 : offset * 0.58),
        center.y,
        thicknessZ
    );

    group.add(
        createLine(thicknessStart, thicknessEnd, lineColor),
        createLine(
            new THREE.Vector3(opposite ? box.min.x : box.max.x, box.min.y, thicknessZ),
            thicknessStart,
            lineColor
        ),
        createLine(
            new THREE.Vector3(opposite ? box.min.x : box.max.x, box.max.y, thicknessZ),
            thicknessEnd,
            lineColor
        ),
        createTickLine(thicknessStart, new THREE.Vector3(1, 0, 0), tick * 0.7, lineColor),
        createTickLine(thicknessEnd, new THREE.Vector3(1, 0, 0), tick * 0.7, lineColor),
        thicknessText
    );

    return group;
};

const createVerticalDimensionGroup = (
    bounds,
    labels,
    fallbackSize,
    visualTheme = getDimensionVisualTheme('light'),
    labelScale = 1.55,
    options = {}
) => {
    const group = new THREE.Group();
    const lineColor = visualTheme.line;
    const box = getSafeBounds(bounds, fallbackSize);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    const opposite = options.placement === 'opposite';
    const visualScale = clamp(options.visualScale, 0.55, 1.2, 1);

    box.getSize(size);
    box.getCenter(center);

    /*
      Для вертикального памятника:
      X = ширина
      Y = высота
      Z = толщина

      Размеры изделия остаются снизу и справа. Размеры заготовки выводятся
      сверху и слева, поэтому оба комплекта читаются одновременно.
    */
    const offset = getDimensionOffset(size.x, size.y, size.z) * visualScale;
    const tick = Math.max(offset * 0.9, 0.022 * visualScale);
    const surfaceZ = opposite ? box.min.z : box.max.z;
    const measureZ = opposite ? box.min.z - offset : box.max.z + offset;
    const widthY = opposite
        ? box.max.y + offset
        : box.min.y + Math.min(Math.max(size.y * 0.015, 0.012), 0.025);
    const heightX = opposite ? box.min.x - offset : box.max.x + offset;
    const thicknessX = opposite ? box.max.x + offset : box.min.x - offset;

    const widthStart = new THREE.Vector3(box.min.x, widthY, measureZ);
    const widthEnd = new THREE.Vector3(box.max.x, widthY, measureZ);
    const widthText = makeTextSprite(labels.x, visualTheme, labelScale);
    widthText.position.set(
        center.x,
        widthY + offset * (opposite ? 0.72 : 0.75),
        measureZ + (opposite ? -offset * 0.42 : offset * 0.45)
    );

    group.add(
        createLine(widthStart, widthEnd, lineColor),
        createLine(
            new THREE.Vector3(box.min.x, opposite ? box.max.y : widthY, surfaceZ),
            widthStart,
            lineColor
        ),
        createLine(
            new THREE.Vector3(box.max.x, opposite ? box.max.y : widthY, surfaceZ),
            widthEnd,
            lineColor
        ),
        createTickLine(widthStart, new THREE.Vector3(0, 1, 0), tick, lineColor),
        createTickLine(widthEnd, new THREE.Vector3(0, 1, 0), tick, lineColor),
        widthText
    );

    const heightStart = new THREE.Vector3(heightX, box.min.y, measureZ);
    const heightEnd = new THREE.Vector3(heightX, box.max.y, measureZ);
    const heightText = makeTextSprite(labels.y, visualTheme, labelScale);
    heightText.position.set(
        heightX + (opposite ? -offset * 0.68 : offset * 0.68),
        center.y,
        measureZ
    );

    group.add(
        createLine(heightStart, heightEnd, lineColor),
        createLine(
            new THREE.Vector3(opposite ? box.min.x : box.max.x, box.min.y, surfaceZ),
            heightStart,
            lineColor
        ),
        createLine(
            new THREE.Vector3(opposite ? box.min.x : box.max.x, box.max.y, surfaceZ),
            heightEnd,
            lineColor
        ),
        createTickLine(heightStart, new THREE.Vector3(1, 0, 0), tick, lineColor),
        createTickLine(heightEnd, new THREE.Vector3(1, 0, 0), tick, lineColor),
        heightText
    );

    const thicknessY = opposite ? box.max.y + offset : widthY;
    const thicknessStart = new THREE.Vector3(thicknessX, thicknessY, box.min.z);
    const thicknessEnd = new THREE.Vector3(thicknessX, thicknessY, box.max.z);
    const thicknessText = makeTextSprite(labels.z, visualTheme, labelScale);
    thicknessText.position.set(
        thicknessX + (opposite ? offset * 0.58 : -offset * 0.58),
        thicknessY + offset * 0.62,
        center.z
    );

    group.add(
        createLine(thicknessStart, thicknessEnd, lineColor),
        createLine(
            new THREE.Vector3(opposite ? box.max.x : box.min.x, opposite ? box.max.y : widthY, box.min.z),
            thicknessStart,
            lineColor
        ),
        createLine(
            new THREE.Vector3(opposite ? box.max.x : box.min.x, opposite ? box.max.y : widthY, box.max.z),
            thicknessEnd,
            lineColor
        ),
        createTickLine(thicknessStart, new THREE.Vector3(1, 0, 0), tick * 0.7, lineColor),
        createTickLine(thicknessEnd, new THREE.Vector3(1, 0, 0), tick * 0.7, lineColor),
        thicknessText
    );

    return group;
};

const createDimensionGroup = (
    bounds,
    sceneDims,
    labels,
    axisPreset,
    visualTheme = getDimensionVisualTheme('light'),
    labelScale = 1.55,
    options = {}
) => {
    if (axisPreset === 'horizontal') {
        return createHorizontalDimensionGroup(
            bounds,
            labels,
            sceneDims,
            visualTheme,
            labelScale,
            options
        );
    }

    return createVerticalDimensionGroup(
        bounds,
        labels,
        sceneDims,
        visualTheme,
        labelScale,
        options
    );
};

const createBlankFrame = (sceneTarget, center, viewMode) => {
    const geometry = new THREE.BoxGeometry(
        Math.max(Number(sceneTarget?.x) || 0.001, 0.001),
        Math.max(Number(sceneTarget?.y) || 0.001, 0.001),
        Math.max(Number(sceneTarget?.z) || 0.001, 0.001)
    );
    const edges = new THREE.EdgesGeometry(geometry);
    geometry.dispose();

    const material = new THREE.LineBasicMaterial({
        color: viewMode === 'dark' ? '#a5b4fc' : '#4f46e5',
        transparent: true,
        opacity: 0.96,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
    });

    const frame = new THREE.LineSegments(edges, material);
    frame.position.copy(center || new THREE.Vector3());
    frame.renderOrder = 25;
    frame.userData.__catalogBlankFrame = true;

    return frame;
};

const disposeMaterial = (material) => {
    if (!material) return;

    if (Array.isArray(material)) {
        material.forEach(disposeMaterial);
        return;
    }

    if (material.map) material.map.dispose?.();
    material.dispose?.();
};

const disposeObject = (object) => {
    if (!object) return;

    object.traverse?.((child) => {
        if (child.geometry && child.userData?.__disposeGeometry !== false) {
            child.geometry.dispose?.();
        }

        if (child.material) {
            disposeMaterial(child.material);
        }

        if (child.userData?.__adminLabelTexture) {
            child.userData.__adminLabelTexture.dispose?.();
        }
    });
};

const AdminProduct3DViewport = ({
    modelData,
    viewMode,
    dimensions,
    blankDimensions = null,
    dimensionMode = 'model',
    showDimensions = true,
    sceneSettings,
    externalCameraStateRef = null,
    showBlankFrame = false,
    realtimeCameraKey = '',
    remoteCameraState = null,
    onRealtimeCameraChange = null,
    materialTexturePreset = null,
    materialTextureBinding = null,
    unpolishedSurfaceTexturePreset = null,
    surfaceFinishPolicy = null,
}) => {
    const mountRef = useRef(null);
    const internalCameraStateRef = useRef(null);
    const cameraStateRef = externalCameraStateRef || internalCameraStateRef;
    const remoteCameraStateRef = useRef(remoteCameraState);
    const localCameraInteractionRef = useRef(false);
    const activeRemoteCameraRef = useRef(null);
    const lastRemoteCameraSequenceRef = useRef(-1);
    const viewportRuntimeRef = useRef(null);

    useEffect(() => {
        remoteCameraStateRef.current = remoteCameraState;
    }, [remoteCameraState]);

    useEffect(() => {
        lastRemoteCameraSequenceRef.current = -1;
        activeRemoteCameraRef.current = null;
    }, [realtimeCameraKey]);

    const normalizedSettings = useMemo(() => (
        normalizeAdminProduct3DSceneSettings(sceneSettings)
    ), [sceneSettings]);

    const dimensionsKey = useMemo(() => {
        if (!dimensions) return 'no-dimensions';

        return [
            Number(dimensions.x || 0).toFixed(4),
            Number(dimensions.y || 0).toFixed(4),
            Number(dimensions.z || 0).toFixed(4),
            Number(dimensions.scaleBase?.x || 0).toFixed(4),
            Number(dimensions.scaleBase?.y || 0).toFixed(4),
            Number(dimensions.scaleBase?.z || 0).toFixed(4),
            dimensions.axisPreset || 'auto',
            dimensions.source || '',
        ].join('|');
    }, [dimensions]);

    const materialTextureKey = useMemo(() => JSON.stringify({
        preset: materialTexturePreset || null,
        unpolishedPreset: unpolishedSurfaceTexturePreset || null,
        binding: materialTextureBinding || null,
        surfaceFinishPolicy: normalizeSurfaceFinishPolicy(surfaceFinishPolicy),
    }), [
        materialTexturePreset,
        unpolishedSurfaceTexturePreset,
        materialTextureBinding,
        surfaceFinishPolicy,
    ]);

    const blankDimensionsKey = useMemo(() => {
        if (!blankDimensions) return 'no-blank-dimensions';

        return [
            Number(blankDimensions.x || 0).toFixed(4),
            Number(blankDimensions.y || 0).toFixed(4),
            Number(blankDimensions.z || 0).toFixed(4),
            blankDimensions.axisPreset || 'auto',
            blankDimensions.source || '',
        ].join('|');
    }, [blankDimensions]);

    useEffect(() => {
        if (!mountRef.current || !modelData?.geometry) return undefined;

        const mount = mountRef.current;
        const theme = getViewerTheme(viewMode);
        const quality = qualityConfig[normalizedSettings.graphicsQuality] || qualityConfig.medium;

        const readMountSize = () => {
            const rect = mount.getBoundingClientRect();
            const measuredWidth = Math.round(
                Number(rect.width || mount.clientWidth || 0)
            );
            const measuredHeight = Math.round(
                Number(rect.height || mount.clientHeight || 0)
            );

            return {
                width: measuredWidth > 0 ? measuredWidth : 900,
                height: measuredHeight > 0 ? measuredHeight : 640,
            };
        };

        const initialMountSize = readMountSize();
        const width = initialMountSize.width;
        const height = initialMountSize.height;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(theme.background);
        scene.fog = new THREE.Fog(theme.background, 18, 48);

        const camera = new THREE.PerspectiveCamera(35, width / height, 0.01, 1000);

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true,
            logarithmicDepthBuffer: true,
        });

        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, quality.pixelRatio));
        renderer.setSize(width, height, false);
        renderer.shadowMap.enabled = !!normalizedSettings.shadowsEnabled;
        renderer.shadowMap.type = quality.shadowType;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 0.82;

        // У гранита не должно быть отдельного «студийного солнца», которое
        // продолжает освещать модель при нулевой яркости пользовательского
        // источника. Блик формирует тот же направленный свет, которым
        // управляют ползунки азимута, высоты и яркости.
        scene.environment = null;

        mount.appendChild(renderer.domElement);

        const hemiLight = new THREE.HemisphereLight(
            '#ffffff',
            theme.hemiGround || '#111827',
            normalizedSettings.hemisphereIntensity
        );
        const ambientLight = new THREE.AmbientLight(
            '#ffffff',
            normalizedSettings.ambientIntensity
        );
        const mainLight = new THREE.DirectionalLight(
            '#ffffff',
            normalizedSettings.sunIntensity
        );
        const fillLight = new THREE.DirectionalLight(
            '#ffffff',
            normalizedSettings.fillIntensity
        );
        const rimLight = new THREE.DirectionalLight(
            '#ffffff',
            normalizedSettings.rimIntensity
        );

        mainLight.castShadow = !!normalizedSettings.shadowsEnabled;
        mainLight.shadow.mapSize.width = quality.shadowSize;
        mainLight.shadow.mapSize.height = quality.shadowSize;
        mainLight.shadow.bias = -0.0004;
        mainLight.shadow.camera.near = 0.01;
        mainLight.shadow.camera.far = 80;
        mainLight.shadow.camera.left = -8;
        mainLight.shadow.camera.right = 8;
        mainLight.shadow.camera.top = 8;
        mainLight.shadow.camera.bottom = -8;

        fillLight.castShadow = false;
        rimLight.castShadow = false;
        fillLight.position.set(-5, 3, -4);
        rimLight.position.set(4, 4, -5);

        /*
         * Только основной DirectionalLight создаёт тень. Заполняющий свет
         * подсвечивает обратную сторону, а контровой отделяет
         * силуэт от фона. Поэтому обратная сторона больше не проваливается
         * в темноту, но красивая основная тень остаётся одной.
         */
        scene.add(
            camera,
            hemiLight,
            ambientLight,
            mainLight,
            fillLight,
            rimLight
        );

        // Основной frontend перед назначением каменной текстуры создаёт
        // box-projected UV, если модель пришла без UV-развёртки. Без этого
        // Three.js берёт один участок JPG для всей поверхности, поэтому
        // памятник выглядит однотонным. Используем ту же логику здесь.
        const geometry = ensureBoxProjectedUVs(modelData.geometry);
        const normalizedMaterials = normalizeMaterials(modelData.materials);
        const materialsArray = (
            Array.isArray(normalizedMaterials)
                ? normalizedMaterials
                : [normalizedMaterials]
        ).map((material) => ensureGranitePhysicalMaterial(material));
        const materials = Array.isArray(normalizedMaterials)
            ? materialsArray
            : materialsArray[0];
        const liveSceneSettings = {
            pixelRatioLimit: quality.pixelRatio,
        };

        viewportRuntimeRef.current = {
            renderer,
            scene,
            camera,
            materialsArray,
            geometry,
            hemiLight,
            ambientLight,
            mainLight,
            fillLight,
            rimLight,
            liveSceneSettings,
            theme,
            panoramaTexture: null,
            panoramaUrl: '',
        };

        const mesh = new THREE.Mesh(geometry, materials);

        mesh.castShadow = !!normalizedSettings.shadowsEnabled;
        mesh.receiveShadow = !!normalizedSettings.shadowsEnabled;
        mesh.userData.__disposeGeometry = false;

        const modelGroup = new THREE.Group();
        const modelRotationX = getAdminModelRotationX(modelData.fileType);

        modelGroup.rotation.set(modelRotationX, 0, 0);
        modelGroup.add(mesh);

        const sourceBox = new THREE.Box3().setFromObject(modelGroup);
        const sourceCenter = new THREE.Vector3();
        const sourceSize = new THREE.Vector3();

        sourceBox.getCenter(sourceCenter);
        sourceBox.getSize(sourceSize);

        modelGroup.position.sub(sourceCenter);

        modelGroup.updateMatrixWorld(true);

        const axisPreset = detectAxisPreset(sourceSize, dimensions?.axisPreset);
        const semanticTarget = getSemanticDimensions(dimensions, sourceSize);
        const sceneTarget = semanticToSceneDimensions(semanticTarget, axisPreset);
        const sceneScale = getScaleToTargetBounds(sourceSize, sceneTarget);
        const labels = getSceneAxisLabels(semanticTarget, axisPreset);

        const blankAxisPreset = detectAxisPreset(
            sourceSize,
            blankDimensions?.axisPreset || axisPreset
        );
        const blankSemanticTarget = blankDimensions
            ? getSemanticDimensions(blankDimensions, sourceSize)
            : null;
        const blankSceneTarget = blankSemanticTarget
            ? semanticToSceneDimensions(blankSemanticTarget, blankAxisPreset)
            : null;
        const blankLabels = blankSemanticTarget
            ? getSceneAxisLabels(blankSemanticTarget, blankAxisPreset)
            : null;

        const displayGroup = new THREE.Group();
        displayGroup.add(modelGroup);
        displayGroup.scale.set(sceneScale.x, sceneScale.y, sceneScale.z);
        scene.add(displayGroup);

        /*
          Масштабируем модель непосредственно до требуемого world-space размера,
          а затем повторно центрируем уже итоговый Box3. Это закрывает модели,
          у которых origin/pivot или вложенная геометрия смещены внутри GLB.
        */
        displayGroup.updateMatrixWorld(true);

        let finalBox = new THREE.Box3().setFromObject(displayGroup);
        const finalCenter = new THREE.Vector3();

        finalBox.getCenter(finalCenter);

        if (
            Number.isFinite(finalCenter.x) &&
            Number.isFinite(finalCenter.y) &&
            Number.isFinite(finalCenter.z) &&
            finalCenter.lengthSq() > 1e-12
        ) {
            displayGroup.position.sub(finalCenter);
            displayGroup.updateMatrixWorld(true);
            finalBox = new THREE.Box3().setFromObject(displayGroup);
        }

        const finalSize = new THREE.Vector3();

        finalBox.getSize(finalSize);

        let blankFrame = null;
        let blankBox = null;

        if (blankSceneTarget) {
            const modelCenter = new THREE.Vector3();
            finalBox.getCenter(modelCenter);

            /*
             * Геометрические границы заготовки рассчитываются всегда.
             * Поэтому её размерные линии можно показывать независимо от
             * самого контура рамки. Низ заготовки совпадает с низом модели.
             */
            const frameCenter = new THREE.Vector3(
                modelCenter.x,
                finalBox.min.y + blankSceneTarget.y / 2,
                modelCenter.z
            );

            blankBox = new THREE.Box3(
                new THREE.Vector3(
                    frameCenter.x - blankSceneTarget.x / 2,
                    frameCenter.y - blankSceneTarget.y / 2,
                    frameCenter.z - blankSceneTarget.z / 2
                ),
                new THREE.Vector3(
                    frameCenter.x + blankSceneTarget.x / 2,
                    frameCenter.y + blankSceneTarget.y / 2,
                    frameCenter.z + blankSceneTarget.z / 2
                )
            );

            if (showBlankFrame) {
                blankFrame = createBlankFrame(
                    blankSceneTarget,
                    frameCenter,
                    viewMode
                );
                scene.add(blankFrame);
            }
        }

        const maxSize = Math.max(
            finalSize.x,
            finalSize.y,
            finalSize.z,
            sceneTarget.x,
            sceneTarget.y,
            sceneTarget.z,
            blankSceneTarget?.x || 0,
            blankSceneTarget?.y || 0,
            blankSceneTarget?.z || 0,
            1
        );

        const distance = maxSize * 2.45;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.enablePan = true;
        controls.enableZoom = true;
        controls.enableRotate = true;
        controls.minDistance = Math.max(maxSize * 0.18, 0.35);
        controls.maxDistance = Math.max(maxSize * 8, 12);

        if (cameraStateRef.current?.position && cameraStateRef.current?.target) {
            camera.position.copy(cameraStateRef.current.position);
            controls.target.copy(cameraStateRef.current.target);
        } else {
            // Основная грань памятника лежит в XY для OBJ/GLB/GLTF.
            // Стартуем почти спереди, но оставляем небольшой угол,
            // чтобы была видна толщина и линейки не накладывались друг на друга.
            camera.position.set(distance * 0.42, distance * 0.48, distance * 1.18);
            controls.target.set(0, 0, 0);
        }

        camera.lookAt(controls.target);
        controls.update();

        const publishCamera = (interacting) => {
            if (!realtimeCameraKey || !onRealtimeCameraChange) return;

            onRealtimeCameraChange({
                viewportKey: realtimeCameraKey,
                position: {
                    x: camera.position.x,
                    y: camera.position.y,
                    z: camera.position.z,
                },
                target: {
                    x: controls.target.x,
                    y: controls.target.y,
                    z: controls.target.z,
                },
                fov: camera.fov,
                interacting,
            });
        };

        let controlPointerActive = false;
        let cameraSettleTimer = null;

        const consumeQueuedRemoteCamera = () => {
            const incoming = remoteCameraStateRef.current;

            if (
                incoming
                && incoming.viewportKey === realtimeCameraKey
            ) {
                lastRemoteCameraSequenceRef.current = Math.max(
                    lastRemoteCameraSequenceRef.current,
                    Number(incoming.sequence || 0)
                );
            }

            activeRemoteCameraRef.current = null;
        };

        const finishLocalCameraInteraction = () => {
            cameraSettleTimer = null;
            consumeQueuedRemoteCamera();
            localCameraInteractionRef.current = false;
            publishCamera(false);
        };

        const scheduleCameraSettle = () => {
            if (cameraSettleTimer) {
                window.clearTimeout(cameraSettleTimer);
            }

            cameraSettleTimer = window.setTimeout(
                finishLocalCameraInteraction,
                CAMERA_INTERACTION_SETTLE_MS
            );
        };

        const handleControlStart = () => {
            controlPointerActive = true;

            if (cameraSettleTimer) {
                window.clearTimeout(cameraSettleTimer);
                cameraSettleTimer = null;
            }

            consumeQueuedRemoteCamera();
            localCameraInteractionRef.current = true;
            publishCamera(true);
        };

        const handleControlChange = () => {
            if (!localCameraInteractionRef.current) return;

            publishCamera(true);

            if (!controlPointerActive) {
                scheduleCameraSettle();
            }
        };

        const handleControlEnd = () => {
            controlPointerActive = false;
            scheduleCameraSettle();
        };

        controls.addEventListener('start', handleControlStart);
        controls.addEventListener('change', handleControlChange);
        controls.addEventListener('end', handleControlEnd);

        const sunDistance = Math.max(maxSize * 5, 5);
        const sunPosition = getSunPosition(
            normalizedSettings,
            sunDistance
        );
        const rimPosition = sunPosition
            .clone()
            .multiplyScalar(-0.78);

        rimPosition.y = Math.max(
            Math.abs(rimPosition.y),
            sunDistance * 0.35
        );

        mainLight.position.copy(sunPosition);
        mainLight.target.position.set(0, 0, 0);
        fillLight.target.position.copy(controls.target);
        rimLight.position.copy(rimPosition);
        rimLight.target.position.copy(controls.target);

        scene.add(
            mainLight.target,
            fillLight.target,
            rimLight.target
        );

        mainLight.target.updateMatrixWorld();
        fillLight.target.updateMatrixWorld();
        rimLight.target.updateMatrixWorld();
        mainLight.shadow.camera.updateProjectionMatrix();

        Object.assign(viewportRuntimeRef.current, {
            mesh,
            controls,
            displayGroup,
            sunDistance,
            maxSize,
        });

        const groundY = Math.min(
            finalBox.min.y,
            blankBox?.min?.y ?? finalBox.min.y
        ) - 0.003;
        const gridSize = Math.max(6, Math.ceil(maxSize * 4));
        const gridDivisions = gridSize;

        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(gridSize, gridSize),
            new THREE.MeshStandardMaterial({
                color: viewMode === 'dark' ? '#111827' : '#d9d3c7',
                roughness: 0.9,
                metalness: 0,
                transparent: true,
                opacity: viewMode === 'dark' ? 0.34 : 0.42,
            })
        );

        ground.rotation.x = -Math.PI / 2;
        ground.position.y = groundY;
        ground.receiveShadow = !!normalizedSettings.shadowsEnabled;
        scene.add(ground);

        const shadowPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(gridSize, gridSize),
            new THREE.ShadowMaterial({
                opacity: normalizedSettings.shadowsEnabled ? theme.shadow : 0,
            })
        );

        shadowPlane.rotation.x = -Math.PI / 2;
        shadowPlane.position.y = groundY + 0.001;
        shadowPlane.receiveShadow = !!normalizedSettings.shadowsEnabled;
        scene.add(shadowPlane);

        const grid = new THREE.GridHelper(
            gridSize,
            gridDivisions,
            viewMode === 'dark' ? '#64748b' : '#94a3b8',
            viewMode === 'dark' ? '#334155' : '#cbd5e1'
        );

        grid.position.y = groundY + 0.004;
        grid.renderOrder = 1;

        if (grid.material) {
            if (Array.isArray(grid.material)) {
                grid.material.forEach((material) => {
                    material.transparent = true;
                    material.opacity = viewMode === 'dark' ? 0.28 : 0.45;
                    material.depthWrite = false;
                });
            } else {
                grid.material.transparent = true;
                grid.material.opacity = viewMode === 'dark' ? 0.28 : 0.45;
                grid.material.depthWrite = false;
            }
        }

        scene.add(grid);

        Object.assign(viewportRuntimeRef.current, {
            ground,
            shadowPlane,
            grid,
        });

        let dimensionGroup = null;

        if (showDimensions) {
            dimensionGroup = new THREE.Group();

            if (dimensionMode === 'model' || dimensionMode === 'both') {
                dimensionGroup.add(
                    createDimensionGroup(
                        finalBox,
                        sceneTarget,
                        labels,
                        axisPreset,
                        getDimensionVisualTheme(viewMode),
                        normalizedSettings.dimensionLabelScale
                    )
                );
            }

            if (
                blankBox
                && blankSceneTarget
                && blankLabels
                && (dimensionMode === 'blank' || dimensionMode === 'both')
            ) {
                dimensionGroup.add(
                    createDimensionGroup(
                        blankBox,
                        blankSceneTarget,
                        blankLabels,
                        blankAxisPreset,
                        {
                            ...getDimensionVisualTheme(viewMode),
                            line: '#7c3aed',
                            labelText: '#6d28d9',
                        },
                        normalizedSettings.dimensionLabelScale * 0.72,
                        {
                            placement: 'opposite',
                            visualScale: 0.72,
                        }
                    )
                );
            }

            scene.add(dimensionGroup);
        }

        let frameId;

        const animate = () => {
            const incoming = remoteCameraStateRef.current;

            if (
                !localCameraInteractionRef.current
                && incoming
                && incoming.viewportKey === realtimeCameraKey
                && Number(incoming.sequence || 0) > lastRemoteCameraSequenceRef.current
            ) {
                lastRemoteCameraSequenceRef.current = Number(incoming.sequence || 0);
                activeRemoteCameraRef.current = {
                    position: new THREE.Vector3(
                        Number(incoming.position?.x || 0),
                        Number(incoming.position?.y || 0),
                        Number(incoming.position?.z || 0)
                    ),
                    target: new THREE.Vector3(
                        Number(incoming.target?.x || 0),
                        Number(incoming.target?.y || 0),
                        Number(incoming.target?.z || 0)
                    ),
                    fov: Number(incoming.fov || camera.fov),
                };
            }

            const remoteTarget = activeRemoteCameraRef.current;
            if (!localCameraInteractionRef.current && remoteTarget) {
                camera.position.lerp(remoteTarget.position, 0.32);
                controls.target.lerp(remoteTarget.target, 0.32);

                const nextFov = THREE.MathUtils.lerp(
                    camera.fov,
                    remoteTarget.fov,
                    0.32
                );

                if (Math.abs(nextFov - camera.fov) > 0.0001) {
                    camera.fov = nextFov;
                    camera.updateProjectionMatrix();
                }

                const positionDone = camera.position.distanceTo(remoteTarget.position) < 0.001;
                const targetDone = controls.target.distanceTo(remoteTarget.target) < 0.001;

                if (positionDone && targetDone) {
                    camera.position.copy(remoteTarget.position);
                    controls.target.copy(remoteTarget.target);
                    camera.fov = remoteTarget.fov;
                    camera.updateProjectionMatrix();
                    activeRemoteCameraRef.current = null;
                }
            }

            controls.update();

            renderer.render(scene, camera);
            frameId = requestAnimationFrame(animate);
        };

        animate();

        let resizeFrameId = null;
        let resizeConfirmFrameId = null;
        let resizeObserver = null;
        const delayedResizeTimers = new Set();
        let renderedWidth = width;
        let renderedHeight = height;
        let renderedPixelRatio = Math.min(
            window.devicePixelRatio || 1,
            liveSceneSettings.pixelRatioLimit
        );

        const applyViewportSize = () => {
            if (!mount.isConnected) return;

            const rect = mount.getBoundingClientRect();
            const nextWidth = Math.round(
                Number(rect.width || mount.clientWidth || 0)
            );
            const nextHeight = Math.round(
                Number(rect.height || mount.clientHeight || 0)
            );

            /*
             * На планшетах CSS-модалка может получить окончательную высоту
             * уже после создания WebGLRenderer. Нулевой или промежуточный
             * размер не применяем, иначе canvas растягивается стилями и
             * изображение выглядит сжатым по вертикали.
             */
            if (!(nextWidth > 0) || !(nextHeight > 0)) return;

            const nextPixelRatio = Math.min(
                window.devicePixelRatio || 1,
                liveSceneSettings.pixelRatioLimit
            );
            const sizeChanged = (
                nextWidth !== renderedWidth
                || nextHeight !== renderedHeight
            );
            const pixelRatioChanged = (
                Math.abs(nextPixelRatio - renderedPixelRatio) > 0.001
            );

            if (!sizeChanged && !pixelRatioChanged) return;

            renderedWidth = nextWidth;
            renderedHeight = nextHeight;
            renderedPixelRatio = nextPixelRatio;

            if (pixelRatioChanged) {
                renderer.setPixelRatio(nextPixelRatio);
            }

            camera.aspect = nextWidth / nextHeight;
            camera.updateProjectionMatrix();

            /*
             * false запрещает Three.js записывать inline width/height.
             * Видимый размер задаёт CSS, а setSize обновляет только реальный
             * буфер WebGL под фактический размер контейнера.
             */
            renderer.setSize(nextWidth, nextHeight, false);
        };

        const scheduleViewportResize = () => {
            if (resizeFrameId) {
                window.cancelAnimationFrame(resizeFrameId);
            }
            if (resizeConfirmFrameId) {
                window.cancelAnimationFrame(resizeConfirmFrameId);
            }

            resizeFrameId = window.requestAnimationFrame(() => {
                resizeFrameId = null;
                applyViewportSize();

                /*
                 * Второй кадр нужен после смены ориентации и открытия
                 * модалки: grid/flex и динамический viewport иногда
                 * завершают перерасчёт на следующем кадре.
                 */
                resizeConfirmFrameId = window.requestAnimationFrame(() => {
                    resizeConfirmFrameId = null;
                    applyViewportSize();
                });
            });
        };

        const scheduleViewportResizeBurst = () => {
            delayedResizeTimers.forEach((timer) => {
                window.clearTimeout(timer);
            });
            delayedResizeTimers.clear();

            scheduleViewportResize();

            [50, 140, 320, 700].forEach((delay) => {
                const timer = window.setTimeout(() => {
                    delayedResizeTimers.delete(timer);
                    scheduleViewportResize();
                }, delay);

                delayedResizeTimers.add(timer);
            });
        };

        if (typeof window.ResizeObserver === 'function') {
            resizeObserver = new window.ResizeObserver(() => {
                scheduleViewportResize();
            });
            resizeObserver.observe(mount);
        }

        window.addEventListener('resize', scheduleViewportResize);
        window.addEventListener('orientationchange', scheduleViewportResizeBurst);
        window.visualViewport?.addEventListener(
            'resize',
            scheduleViewportResize
        );

        /*
         * Первый перерасчёт делаем серией: на Android-планшетах изменение
         * адресной строки, dvh и горизонтальной раскладки приходит не одним
         * событием и может завершиться после первого paint.
         */
        scheduleViewportResizeBurst();

        return () => {
            cameraStateRef.current = {
                position: camera.position.clone(),
                target: controls.target.clone(),
            };

            cancelAnimationFrame(frameId);

            if (resizeFrameId) {
                window.cancelAnimationFrame(resizeFrameId);
                resizeFrameId = null;
            }
            if (resizeConfirmFrameId) {
                window.cancelAnimationFrame(resizeConfirmFrameId);
                resizeConfirmFrameId = null;
            }

            delayedResizeTimers.forEach((timer) => {
                window.clearTimeout(timer);
            });
            delayedResizeTimers.clear();

            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleViewportResize);
            window.removeEventListener(
                'orientationchange',
                scheduleViewportResizeBurst
            );
            window.visualViewport?.removeEventListener(
                'resize',
                scheduleViewportResize
            );

            controls.removeEventListener('start', handleControlStart);
            controls.removeEventListener('change', handleControlChange);
            controls.removeEventListener('end', handleControlEnd);

            if (cameraSettleTimer) {
                window.clearTimeout(cameraSettleTimer);
                cameraSettleTimer = null;
            }

            localCameraInteractionRef.current = false;
            controls.dispose();

            if (realtimeCameraKey && onRealtimeCameraChange) {
                onRealtimeCameraChange({
                    viewportKey: realtimeCameraKey,
                    clear: true,
                });
            }

            if (renderer.domElement && renderer.domElement.parentNode) {
                renderer.domElement.parentNode.removeChild(renderer.domElement);
            }

            if (viewportRuntimeRef.current?.renderer === renderer) {
                viewportRuntimeRef.current.panoramaTexture?.dispose?.();
                viewportRuntimeRef.current.panoramaTexture = null;
                viewportRuntimeRef.current = null;
            }

            renderer.renderLists?.dispose?.();
            renderer.dispose();
            renderer.forceContextLoss?.();

            disposeMaterial(materials);

            if (dimensionGroup) {
                disposeObject(dimensionGroup);
            }

            if (blankFrame) {
                disposeObject(blankFrame);
            }

            ground.geometry.dispose();
            disposeMaterial(ground.material);

            shadowPlane.geometry.dispose();
            disposeMaterial(shadowPlane.material);

            grid.geometry.dispose();
            disposeMaterial(grid.material);

            scene.environment = null;
            scene.clear();
        };
    }, [
        modelData,
        viewMode,
        dimensionsKey,
        blankDimensionsKey,
        dimensionMode,
        showDimensions,
        normalizedSettings.dimensionLabelScale,
        externalCameraStateRef,
        showBlankFrame,
        realtimeCameraKey,
        onRealtimeCameraChange,
    ]);

    /*
     * Световые ползунки и качество обновляют уже существующую сцену.
     * WebGLRenderer, камера, mesh и animation loop не пересоздаются.
     */
    useEffect(() => {
        const runtime = viewportRuntimeRef.current;

        if (!runtime?.renderer || !runtime?.scene) return;

        const {
            renderer,
            camera,
            controls,
            mainLight,
            fillLight,
            rimLight,
            hemiLight,
            ambientLight,
            mesh,
            ground,
            shadowPlane,
            grid,
            liveSceneSettings,
            sunDistance = 5,
            theme,
        } = runtime;
        const quality = (
            qualityConfig[normalizedSettings.graphicsQuality]
            || qualityConfig.medium
        );
        const sunPosition = getSunPosition(
            normalizedSettings,
            sunDistance
        );
        const rimPosition = sunPosition
            .clone()
            .multiplyScalar(-0.78);

        rimPosition.y = Math.max(
            Math.abs(rimPosition.y),
            sunDistance * 0.35
        );

        mainLight.intensity = normalizedSettings.sunIntensity;
        fillLight.intensity = normalizedSettings.fillIntensity;
        rimLight.intensity = normalizedSettings.rimIntensity;
        hemiLight.intensity = normalizedSettings.hemisphereIntensity;
        ambientLight.intensity = normalizedSettings.ambientIntensity;

        mainLight.position.copy(sunPosition);
        mainLight.target.position.copy(controls?.target || new THREE.Vector3());
        rimLight.position.copy(rimPosition);
        rimLight.target.position.copy(controls?.target || new THREE.Vector3());

        /*
         * Заполняющий свет остаётся в мировой сцене и находится с обратной
         * стороны основного солнца. Он не копирует позицию OrbitCamera и
         * вообще не обновляется внутри requestAnimationFrame.
         */
        fillLight.position.set(
            -sunPosition.x * 0.62,
            Math.max(
                sunDistance * 0.48,
                Math.abs(sunPosition.y) * 0.55
            ),
            -sunPosition.z * 0.62
        );
        fillLight.target.position.copy(
            controls?.target || new THREE.Vector3()
        );

        mainLight.target.updateMatrixWorld();
        fillLight.target.updateMatrixWorld();
        rimLight.target.updateMatrixWorld();

        const shadowsEnabled = !!normalizedSettings.shadowsEnabled;

        renderer.shadowMap.enabled = shadowsEnabled;
        renderer.shadowMap.type = quality.shadowType;
        mainLight.castShadow = shadowsEnabled;
        mesh.castShadow = shadowsEnabled;
        mesh.receiveShadow = shadowsEnabled;

        if (ground) {
            ground.visible = !!normalizedSettings.showGround;
            ground.receiveShadow = (
                shadowsEnabled
                && normalizedSettings.showGround
            );
        }

        if (grid) {
            grid.visible = !!normalizedSettings.showGround;
        }

        if (shadowPlane) {
            shadowPlane.visible = !!normalizedSettings.showGround;
            shadowPlane.receiveShadow = (
                shadowsEnabled
                && normalizedSettings.showGround
            );

            if (shadowPlane.material) {
                shadowPlane.material.opacity = (
                    shadowsEnabled
                    && normalizedSettings.showGround
                ) ? Number(theme?.shadow || 0.28) : 0;
                shadowPlane.material.needsUpdate = true;
            }
        }

        const previousShadowSize = Number(
            mainLight.shadow.mapSize?.width || 0
        );

        if (previousShadowSize !== quality.shadowSize) {
            mainLight.shadow.mapSize.set(
                quality.shadowSize,
                quality.shadowSize
            );
            mainLight.shadow.map?.dispose?.();
            mainLight.shadow.map = null;
        }

        liveSceneSettings.pixelRatioLimit = quality.pixelRatio;

        const nextPixelRatio = Math.min(
            window.devicePixelRatio || 1,
            liveSceneSettings.pixelRatioLimit
        );

        renderer.setPixelRatio(nextPixelRatio);

        const mount = mountRef.current;
        const rect = mount?.getBoundingClientRect?.();

        if (rect?.width > 0 && rect?.height > 0) {
            camera.aspect = rect.width / rect.height;
            camera.updateProjectionMatrix();
            renderer.setSize(rect.width, rect.height, false);
        }

        renderer.shadowMap.needsUpdate = true;
    }, [
        viewMode,
        normalizedSettings.sunAngle,
        normalizedSettings.sunVertical,
        normalizedSettings.sunIntensity,
        normalizedSettings.fillIntensity,
        normalizedSettings.hemisphereIntensity,
        normalizedSettings.ambientIntensity,
        normalizedSettings.rimIntensity,
        normalizedSettings.shadowsEnabled,
        normalizedSettings.graphicsQuality,
        normalizedSettings.showGround,
    ]);

    /*
     * Обычный фон берётся из текущей темы. Для 360° используется
     * equirectangular JPG/PNG; его можно одновременно назначить environment,
     * чтобы полированный гранит получал естественные отражения.
     */
    useEffect(() => {
        const runtime = viewportRuntimeRef.current;

        if (!runtime?.scene || !runtime?.renderer) return undefined;

        const {
            scene,
            renderer,
        } = runtime;
        const theme = getViewerTheme(viewMode);
        const panoramaUrl = String(
            normalizedSettings.backgroundUrl || ''
        ).trim();
        const usePanorama = (
            normalizedSettings.backgroundMode === 'panorama'
            && panoramaUrl
        );
        let cancelled = false;
        let loadedTexture = null;

        runtime.panoramaTexture?.dispose?.();
        runtime.panoramaTexture = null;
        runtime.panoramaUrl = '';
        scene.environment = null;

        if (!usePanorama) {
            scene.background = new THREE.Color(theme.background);
            scene.fog = new THREE.Fog(theme.background, 18, 48);

            return undefined;
        }

        scene.background = new THREE.Color(theme.background);
        scene.fog = new THREE.Fog(theme.background, 18, 48);

        const loader = new THREE.TextureLoader();

        loader.load(
            resolveAnyStaticUrl(panoramaUrl),
            (texture) => {
                if (
                    cancelled
                    || viewportRuntimeRef.current?.renderer !== renderer
                ) {
                    texture.dispose?.();
                    return;
                }

                loadedTexture = limitPanoramaTextureSize(texture);
                loadedTexture.mapping = THREE.EquirectangularReflectionMapping;
                loadedTexture.colorSpace = THREE.SRGBColorSpace;
                loadedTexture.minFilter = THREE.LinearFilter;
                loadedTexture.magFilter = THREE.LinearFilter;
                loadedTexture.generateMipmaps = false;
                loadedTexture.needsUpdate = true;

                scene.background = loadedTexture;
                scene.environment = (
                    normalizedSettings.backgroundAsEnvironment
                        ? loadedTexture
                        : null
                );
                scene.fog = null;

                runtime.panoramaTexture = loadedTexture;
                runtime.panoramaUrl = panoramaUrl;
            },
            undefined,
            () => {
                if (
                    cancelled
                    || viewportRuntimeRef.current?.renderer !== renderer
                ) {
                    return;
                }

                scene.background = new THREE.Color(theme.background);
                scene.environment = null;
                scene.fog = new THREE.Fog(
                    theme.background,
                    18,
                    48
                );
            }
        );

        return () => {
            cancelled = true;

            if (
                loadedTexture
                && runtime.panoramaTexture === loadedTexture
            ) {
                runtime.panoramaTexture = null;
                runtime.panoramaUrl = '';
                scene.environment = null;
                loadedTexture.dispose?.();
            }
        };
    }, [
        viewMode,
        normalizedSettings.backgroundMode,
        normalizedSettings.backgroundUrl,
        normalizedSettings.backgroundAsEnvironment,
    ]);

    /*
     * Смена породы камня больше не пересоздаёт WebGLRenderer, камеру,
     * геометрию, тени и весь requestAnimationFrame-цикл. Меняются только
     * материалы текущего runtime.
     */
    useEffect(() => {
        const runtime = viewportRuntimeRef.current;
        const renderer = runtime?.renderer;
        const materialsArray = runtime?.materialsArray;
        const geometry = runtime?.geometry;

        if (!renderer || !Array.isArray(materialsArray) || !materialsArray.length) {
            return undefined;
        }

        const hasGraniteTexturePreset = [
            materialTexturePreset,
            unpolishedSurfaceTexturePreset,
        ].some((preset) => (
            String(preset?.path || preset?.url || '').trim()
        ));

        /*
         * GLB может содержать формально корректный uv-атрибут, рассчитанный
         * под исходный атлас или однотонный материал. Для произвольной
         * гранитной JPG такая развёртка часто выбирает один маленький участок
         * изображения — памятник выглядит сплошным коричневым/серым цветом.
         *
         * Гранит в админке является процедурной заменой исходного материала,
         * поэтому перед его назначением принудительно строим box-projected UV.
         * Операция идемпотентна и не пересоздаёт renderer или сам mesh.
         */
        if (hasGraniteTexturePreset && geometry) {
            ensureBoxProjectedUVs(geometry, { force: true });
        }

        const textureBindingMode = materialTextureBinding?.mode === 'manual'
            ? 'manual'
            : 'auto';
        const applyToAllMaterials = materialTextureBinding?.applyToAll !== false;
        const selectedMaterialIndexes = new Set(
            (
                Array.isArray(materialTextureBinding?.materialIndexes)
                    ? materialTextureBinding.materialIndexes
                    : []
            ).map((value) => Math.max(
                0,
                Math.floor(Number(value) || 0)
            ))
        );
        const normalizedSurfaceFinishPolicy = normalizeSurfaceFinishPolicy(
            surfaceFinishPolicy
        );
        const shouldApplyStoneToIndex = (index) => (
            textureBindingMode === 'auto'
            || applyToAllMaterials
            || selectedMaterialIndexes.has(index)
        );
        const effectiveFinishByIndex = materialsArray.map(
            (material, index) => getEffectiveSurfaceFinish({
                material,
                materialIndex: index,
                policy: normalizedSurfaceFinishPolicy,
            })
        );
        const loadedBaseTextures = [];
        let disposed = false;

        materialsArray.forEach((material, index) => {
            if (!material) return;

            const finish = effectiveFinishByIndex[index];
            const finishPreset = finish === SURFACE_FINISH.UNPOLISHED
                ? unpolishedSurfaceTexturePreset
                : materialTexturePreset;

            applySurfaceFinishToThreeMaterial(
                material,
                finish,
                finishPreset
            );
        });

        const applyTexturePreset = (preset, shouldApplyToIndex) => {
            const texturePath = String(
                preset?.path || preset?.url || ''
            ).trim();

            if (!texturePath) return;

            const textureLoader = new THREE.TextureLoader();
            const revision = Number(preset?.catalogRevision || 0);
            const versionedTexturePath = `${texturePath}${texturePath.includes('?') ? '&' : '?'}v=${encodeURIComponent(revision)}`;

            textureLoader.load(
                resolveAnyStaticUrl(versionedTexturePath),
                (baseTexture) => {
                    if (disposed) {
                        baseTexture.dispose?.();
                        return;
                    }

                    loadedBaseTextures.push(baseTexture);
                    baseTexture.colorSpace = THREE.SRGBColorSpace;
                    baseTexture.wrapS = THREE.RepeatWrapping;
                    baseTexture.wrapT = THREE.RepeatWrapping;
                    baseTexture.flipY = false;
                    baseTexture.anisotropy = Math.min(
                        8,
                        renderer.capabilities.getMaxAnisotropy()
                    );
                    baseTexture.needsUpdate = true;

                    materialsArray.forEach((material, index) => {
                        if (
                            !material
                            || !shouldApplyToIndex(index)
                            || disposed
                        ) {
                            return;
                        }

                        const texture = baseTexture.clone();

                        texture.image = baseTexture.image;
                        texture.colorSpace = THREE.SRGBColorSpace;
                        texture.wrapS = THREE.RepeatWrapping;
                        texture.wrapT = THREE.RepeatWrapping;
                        texture.repeat.set(
                            Number(preset?.repeat?.[0] || 1),
                            Number(preset?.repeat?.[1] || 1)
                        );
                        texture.offset.set(
                            Number(preset?.offset?.[0] || 0),
                            Number(preset?.offset?.[1] || 0)
                        );
                        texture.center.set(0.5, 0.5);
                        texture.rotation = Number(preset?.rotation || 0);
                        texture.flipY = false;
                        texture.anisotropy = baseTexture.anisotropy;
                        texture.needsUpdate = true;

                        material.map?.dispose?.();
                        material.map = texture;

                        if (material.color?.isColor) {
                            material.color.setHex(0xffffff);
                        }
                        if (material.emissive?.isColor) {
                            material.emissive.setHex(0x000000);
                        }
                        if (
                            typeof material.emissiveIntensity === 'number'
                        ) {
                            material.emissiveIntensity = 0;
                        }

                        material.transparent = false;
                        material.opacity = 1;

                        [
                            'normalMap',
                            'roughnessMap',
                            'metalnessMap',
                            'aoMap',
                            'emissiveMap',
                            'alphaMap',
                            'bumpMap',
                            'displacementMap',
                            'envMap',
                        ].forEach((mapKey) => {
                            material[mapKey]?.dispose?.();
                            material[mapKey] = null;
                        });

                        applySurfaceFinishToThreeMaterial(
                            material,
                            effectiveFinishByIndex[index],
                            preset
                        );
                        material.needsUpdate = true;
                    });
                },
                undefined,
                () => undefined
            );
        };

        applyTexturePreset(
            unpolishedSurfaceTexturePreset,
            (index) => (
                effectiveFinishByIndex[index]
                === SURFACE_FINISH.UNPOLISHED
            )
        );

        applyTexturePreset(
            materialTexturePreset,
            (index) => (
                effectiveFinishByIndex[index]
                !== SURFACE_FINISH.UNPOLISHED
                && shouldApplyStoneToIndex(index)
            )
        );

        return () => {
            disposed = true;
            loadedBaseTextures.forEach(
                (texture) => texture?.dispose?.()
            );
        };
    }, [
        modelData,
        viewMode,
        dimensionsKey,
        blankDimensionsKey,
        dimensionMode,
        showDimensions,
        normalizedSettings.dimensionLabelScale,
        externalCameraStateRef,
        showBlankFrame,
        realtimeCameraKey,
        onRealtimeCameraChange,
        materialTextureKey,
        materialTexturePreset,
        unpolishedSurfaceTexturePreset,
        materialTextureBinding,
        surfaceFinishPolicy,
    ]);

    return <div className="adminProduct3DPreview__canvas" ref={mountRef} />;
};

export default AdminProduct3DViewport;
